import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import fetchPuzzlesByDate from '../services/mockPuzzleApi';
import { validatePuzzleData } from '../utils/puzzleValidation';

export interface PuzzleDetail {
    difficulty: string;
    result: string;
    first_move: string;
    fen: string;
    solved?: string;
}

export interface Puzzle {
    easy: PuzzleDetail;
    medium: PuzzleDetail;
    hard: PuzzleDetail;
}

const defaultPuzzle: Puzzle = {
    easy: { difficulty: 'easy', result: 'win', first_move: '', fen: '8/8/8/8/8/8/8/8 w - - 0 1' },
    medium: { difficulty: 'medium', result: 'win', first_move: '', fen: '8/8/8/8/8/8/8/8 w - - 0 1' },
    hard: { difficulty: 'hard', result: 'win', first_move: '', fen: '8/8/8/8/8/8/8/8 w - - 0 1' },
};

export const usePuzzle = (
    dateParam: string | undefined,
    difficultyParam: 'easy' | 'medium' | 'hard' | undefined
) => {
    const navigate = useNavigate();
    const [puzzle, setPuzzle] = useState<Puzzle>(defaultPuzzle);
    const [loading, setLoading] = useState(true);

    // Initialize state from params or defaults
    const [selectedDifficulty, setSelectedDifficulty] = useState<'easy' | 'medium' | 'hard'>(
        difficultyParam || 'easy'
    );

    // Parse date param safely
    const initialDate = dateParam ? new Date(dateParam) : new Date();
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        !isNaN(initialDate.getTime()) ? initialDate : new Date()
    );

    useEffect(() => {
        // If params change, sync local state (e.g. browser back button)
        if (difficultyParam) setSelectedDifficulty(difficultyParam);

        if (dateParam) {
            const parsed = new Date(dateParam);
            if (!isNaN(parsed.getTime())) {
                setSelectedDate(parsed);
            }
        }
    }, [dateParam, difficultyParam]);

    useEffect(() => {
        const fetchPuzzle = async () => {
            setLoading(true);
            const dateToUse = selectedDate || new Date();
            const formattedDate = dateToUse.toISOString().split('T')[0];
            const storedPuzzleKey = `puzzle-${formattedDate}`;

            try {
                // ALWAYS fetch fresh data from backend
                const puzzleData = await fetchPuzzlesByDate(formattedDate);
                const validated = validatePuzzleData(puzzleData);

                // Check local storage ONLY for 'solved' status to preserve progress
                const storedString = localStorage.getItem(storedPuzzleKey);
                if (storedString) {
                    try {
                        const stored = JSON.parse(storedString);
                        // Handle potential legacy or timestamped format
                        const storedData = (stored.timestamp && stored.data) ? stored.data : stored;

                        if (storedData?.easy?.solved) validated.easy.solved = storedData.easy.solved;
                        if (storedData?.medium?.solved) validated.medium.solved = storedData.medium.solved;
                        if (storedData?.hard?.solved) validated.hard.solved = storedData.hard.solved;
                    } catch (e) {
                        console.warn('Failed to recover progress from cache', e);
                    }
                }

                setPuzzle(validated);

                // Save fresh data + recovered progress back to storage
                localStorage.setItem(storedPuzzleKey, JSON.stringify({
                    timestamp: Date.now(),
                    data: validated
                }));

            } catch (error) {
                console.error('Error fetching puzzle:', error);
                setPuzzle(defaultPuzzle);
            } finally {
                setLoading(false);
            }
        };

        fetchPuzzle();
    }, [selectedDate]);

    const changeDate = (newDate: Date | null) => {
        if (newDate && newDate <= new Date()) {
            setSelectedDate(newDate);
            const formattedDate = newDate.toISOString().split('T')[0];
            navigate(`/puzzle/${formattedDate}/${selectedDifficulty}`);
        }
    };

    const changeDifficulty = (newDifficulty: 'easy' | 'medium' | 'hard') => {
        setSelectedDifficulty(newDifficulty);
        if (selectedDate) {
            const formattedDate = selectedDate.toISOString().split('T')[0];
            navigate(`/puzzle/${formattedDate}/${newDifficulty}`);
        }
    };

    const onPuzzleSolved = (difficulty: 'easy' | 'medium' | 'hard') => {
        setPuzzle(prev => ({
            ...prev,
            [difficulty]: {
                ...prev[difficulty],
                solved: 'yes'
            }
        }));
    };

    return {
        puzzle,
        loading,
        selectedDate,
        selectedDifficulty,
        changeDate,
        changeDifficulty,
        onPuzzleSolved,
    };
};
